// Keyless pickup-route helpers shared by the web admin dashboard.
// Maps render with Leaflet + OpenStreetMap tiles (no API key / billing); navigation hands off to Google Maps.

export function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Greedy nearest-next ordering starting from the shop. Returns stops with a legKm field.
export function orderPickups(shop, stops) {
  const remaining = stops.slice();
  const ordered = [];
  let from = shop;
  while (remaining.length) {
    let bestIdx = 0;
    let bestD = Infinity;
    remaining.forEach((s, i) => {
      const d = haversineKm(from, s.location);
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push({ ...next, legKm: bestD });
    from = next.location;
  }
  return ordered;
}

// Road-distance ordering via the free, keyless OSRM public server (no API key / billing). Builds a
// driving-distance matrix from the shop + all stops, then greedy nearest-next on real road distance.
// Falls back silently to straight-line orderPickups if OSRM is unreachable, so the feature never breaks.
export async function orderPickupsByRoad(shop, stops) {
  if (!stops || !stops.length || !shop) return orderPickups(shop || { lat: 0, lng: 0 }, stops || []);
  try {
    const coords = [shop, ...stops.map((s) => s.location)].map((p) => `${p.lng},${p.lat}`).join(";");
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 7000);
    const res = await fetch(`https://router.project-osrm.org/table/v1/driving/${coords}?annotations=distance`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) throw new Error("osrm");
    const data = await res.json();
    const dist = data.distances;
    if (!Array.isArray(dist) || !dist.length) throw new Error("no matrix");
    const n = stops.length;
    const used = new Array(n).fill(false);
    const ordered = [];
    let fromIdx = 0; // 0 = shop
    for (let k = 0; k < n; k++) {
      let best = -1;
      let bestD = Infinity;
      for (let j = 0; j < n; j++) {
        if (used[j]) continue;
        const d = dist[fromIdx] ? dist[fromIdx][j + 1] : null;
        if (d == null) continue;
        if (d < bestD) { bestD = d; best = j; }
      }
      if (best < 0) break;
      used[best] = true;
      ordered.push({ ...stops[best], legKm: bestD / 1000 });
      fromIdx = best + 1;
    }
    if (ordered.length < n) {
      const remaining = stops.filter((_, j) => !used[j]);
      const tail = orderPickups(ordered.length ? ordered[ordered.length - 1].location : shop, remaining);
      ordered.push(...tail);
    }
    return ordered;
  } catch {
    return orderPickups(shop, stops);
  }
}

// Google Maps turn-by-turn through all stops, in order, starting from the shop.
export function googleRouteUrl(shop, ordered) {
  if (!ordered.length) return "";
  const pts = ordered.map((s) => `${s.location.lat},${s.location.lng}`);
  const destination = pts[pts.length - 1];
  const waypoints = pts.slice(0, -1).join("|");
  let url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
  if (shop?.lat != null && shop?.lng != null) url += `&origin=${shop.lat},${shop.lng}`;
  if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
  return url;
}

export const mapsDirUrl = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

// Self-contained Leaflet map HTML (for an <iframe srcDoc>). markers: [{lat,lng,label}].
export function leafletHtml(markers, { zoom = 15, line = false } = {}) {
  const pts = JSON.stringify(markers || []);
  return `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"/>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<style>html,body,#m{height:100%;width:100%;margin:0;padding:0;background:#e8ede9}.leaflet-tooltip{font:600 11px sans-serif;padding:1px 5px}</style></head>
<body><div id="m"></div>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<script>
var pts=${pts};
var map=L.map('m',{zoomControl:false,attributionControl:false,dragging:true});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:19}).addTo(map);
var g=[];
pts.forEach(function(p){var m=L.marker([p.lat,p.lng]).addTo(map);if(p.label!==undefined&&p.label!=='')m.bindTooltip(String(p.label),{permanent:true,direction:'top'});g.push([p.lat,p.lng]);});
if(${line ? "true" : "false"} && g.length>1){L.polyline(g,{color:'#2E5C45',weight:3,opacity:0.8}).addTo(map);}
if(g.length===0){map.setView([20,80],4);}else if(g.length===1){map.setView(g[0],${zoom});}else{map.fitBounds(g,{padding:[28,28]});}
</script></body></html>`;
}
